import type { ColumnSizingInfoState, ColumnSizingState, Header } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { Separator } from "../Separator";
import { ColumnHeaderFilterDropdown } from "./ColumnHeaderFilterDropdown";

export const FilterableHeaderCell = <TData, TValue>({
	header,
	columnSizingInfo,
	columnSizing,
	setColumnSizing,
	className,
}: {
	header: Header<TData, TValue>;
	columnSizingInfo: ColumnSizingInfoState;
	columnSizing: ColumnSizingState[keyof ColumnSizingState];
	setColumnSizing: (columnSize: number) => void;
	className?: string;
}) => {
	const cellRef = useRef<HTMLDivElement>(null);
	const dropdownTriggerRef = useRef<HTMLButtonElement>(null);
	const clickXRef = useRef<number | undefined>(undefined);
	const setColumnSizingRef = useRef(setColumnSizing);
	setColumnSizingRef.current = setColumnSizing;
	const hasDropdown =
		header.column.getCanSort() || header.column.columnDef.meta?.headerFilterType !== undefined;
	const columnWidth = header.column.getSize();

	useEffect(() => {
		if (cellRef.current && (columnSizing == null || columnSizing < cellRef.current.clientWidth)) {
			setColumnSizingRef.current(cellRef.current.clientWidth);
		}
	}, [columnSizing]);

	useEffect(() => {
		if (
			columnSizingInfo.isResizingColumn === header.id &&
			columnSizingInfo.startSize !== null &&
			columnSizingInfo.deltaOffset !== null
		) {
			const newSize = columnSizingInfo.startSize + columnSizingInfo.deltaOffset;
			if (newSize !== columnSizing) {
				setColumnSizingRef.current(newSize);
			}
		}
	}, [
		columnSizingInfo.isResizingColumn,
		columnSizingInfo.startSize,
		columnSizingInfo.deltaOffset,
		header.id,
		columnSizing,
	]);

	return (
		<div
			className={cn(
				"h-full flex min-w-0 items-center text-[13px] font-medium text-secondary pl-[15px] cursor-pointer truncate",
				header.column.id === "expand" ? "px-0" : "",
				className,
				"[&:nth-last-child(1)]:flex-1",
			)}
			ref={cellRef}
			style={{ width: columnWidth, minWidth: header.column.columnDef.minSize }}
		>
			<div
				className={cn(
					"flex-1 flex items-center h-full",
					header.getContext().column.columnDef.header ? "justify-between" : "justify-center",
				)}
				onClick={
					hasDropdown
						? (e: React.MouseEvent) => {
								clickXRef.current = e.clientX;
								dropdownTriggerRef.current?.click();
							}
						: undefined
				}
			>
				<div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
				{hasDropdown && (
					<ColumnHeaderFilterDropdown
						column={header.column}
						triggerRef={dropdownTriggerRef}
						clickXRef={clickXRef}
						anchorRef={cellRef}
					/>
				)}
			</div>
			<div className="px-1 cursor-ew-resize" onMouseDown={header.getResizeHandler()}>
				<Separator orientation="vertical" className="h-[18px] bg-bc-strong" />
			</div>
		</div>
	);
};
